import { IStudentRow, TagType } from 'api-paths-cas';
import deepEqual from 'fast-deep-equal/es6/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box } from '@chakra-ui/react';

import { ApplicationState } from '../../../store';
import { UserRole } from '../../../store/auth/types';
import {
  setSelectedRows as dashboardActions_setSelectedRows,
  clearStudents,
  requestStudents,
  requestTotals,
  requestFilters,
  setIsTaskBarOpen,
  studentsUnpinTags,
  setIsDropdownDisabled,
  setTabs,
} from '../../../store/dashboard/actions';
import { getPartner } from '../../../store/config/actions';
import {
  getHasAgentOnlyDocs,
  getHasPathwayids,
  getIsAdminOrEnroly,
  getIsImmigrationProvider,
  getIsOfferEnabled,
  getIsPartner,
  getPartners,
  getRole,
  getSelectedRows,
  getTabs,
  getIntakeId,
} from '../../../store/selectors';
import { getStudentUserRecord } from '../../../store/studentUsers/actions';
import { setScrollRowIndex } from '../../../store/ui/actions';
import useDebouncedCallback from '../../../utilities/customHooks/useDebouncedCallback';
import { getActiveStageTab } from '../../../utilities/helpers';
import { makeTabs } from '../../pages/uni-dashboard/left-bar/tabs/makeTabs';
import { getColumnsForActiveTab } from './grid-columns';
import { RowEventsHandler } from './grid-columns/formatters';
import { serializeStudentsToRows } from './rows';
import { Row, SortDirection } from './types';
import AlertDialog from '../../pages/modals/AlertDialog';
import { CASToolbar } from '../CASToolbar';
import { QueryKeys } from '@/types';
import { DEFAULT_STAGE_TAB } from '@/utilities/constants';

const LEFT_BAR_WIDTH = 200;

const CustomDataGridComp = () => {
  const dispatch = useDispatch();
  const gridRef: any = useRef(null);
  const gridHeaderRef: any = useRef(null);

  const scrollRowIndex = useSelector(({ ui }: ApplicationState) => ui.uniDashRowScrollIndex || 0, deepEqual);
  const allStudents = useSelector(({ dashboard }: ApplicationState) => dashboard.students, deepEqual);
  const studentReloaded = useSelector(
    ({ dashboard }: ApplicationState) => dashboard.allStudentsLoaded || false,
    deepEqual,
  );
  const totals = useSelector(({ dashboard }: ApplicationState) => dashboard.totals);
  const allSelected = useSelector(({ dashboard }: ApplicationState) => dashboard.allSelected);

  const configTabs = useSelector(
    ({ auth, config }: ApplicationState) =>
      auth.role === UserRole.Unknown ? [] : (config.dashboardTabs[auth.role] ?? []),
    deepEqual,
  );
  const pathwayAccess = useSelector(({ auth }: ApplicationState) => auth.pathwayAccess, deepEqual);

  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const headerTab = useSelector(({ ui }: ApplicationState) => ui.headerTab, deepEqual);

  const tabs = useSelector(getTabs);
  const userRole = useSelector(getRole);
  const isPathway = useSelector(getHasPathwayids);
  const activeTab = getActiveStageTab(params, headerTab, DEFAULT_STAGE_TAB, QueryKeys.STAGE);
  const partners = useSelector(getPartners);
  const isEnrolyUser = useSelector(getIsAdminOrEnroly);
  const isServiceProvider = useSelector(getIsPartner);
  const isImmigrationProvider = useSelector(getIsImmigrationProvider);
  const isOfferEnabled = useSelector(getIsOfferEnabled);
  const selectedRows = useSelector(getSelectedRows);
  const hasAgentOnlyDocs = useSelector(getHasAgentOnlyDocs);
  const isAgentUser = [UserRole.Agent, UserRole.AgencyAdmin].includes(userRole) ?? false;
  const intakeId = useSelector(getIntakeId);
  const authUni = useSelector(({ auth }: ApplicationState) => auth.uni);
  const authIntake = useSelector(({ auth }: ApplicationState) => auth.intake);
  const currentUserName = useSelector(({ auth }: ApplicationState) => auth.student_number);
  const isConfigLoaded = useSelector(({ config }: ApplicationState) => config.isLoaded);
  const availableIntakes = useSelector(({ config }: ApplicationState) => config.intakeIds || []);

  const resetDropdownStatus = useSelector(
    ({ dashboard }: ApplicationState) => dashboard.resetDropdownStatus,
    deepEqual,
  );
  const openedStudentRecords = useSelector(
    ({ dashboard }: ApplicationState) => dashboard.openedStudentRecords,
    deepEqual,
  );
  const studentUsers = useSelector(({ studentUsers }: ApplicationState) => studentUsers, deepEqual);

  const navigate = useNavigate();
  const updateScrollRowIndex = useCallback((idx: number) => dispatch(setScrollRowIndex(idx)), [dispatch]);

  const setSelectedRows = useCallback(
    (selectedRows: string[]) => dispatch(dashboardActions_setSelectedRows(selectedRows)),
    [dispatch],
  );

  const setSort = useCallback(
    (sort: { col?: string; direction?: string }) => {
      const urlParams = new URLSearchParams(location.search);
      if (sort?.direction === 'ASC' || sort?.direction === 'DESC') {
        urlParams.set('sort-col', sort?.col ?? '');
        urlParams.set('sort-dir', sort?.direction ?? '');
      } else {
        urlParams.delete('sort-col');
        urlParams.delete('sort-dir');
      }
      if (urlParams.toString() !== location.search.replace(/^\?/, '')) {
        navigate({ search: urlParams.toString() }, { replace: true });
      }
    },
    [dispatch, navigate, location.search],
  );

  // Track if this is initial page load - use ref to persist across renders
  const isInitialLoadRef = useRef(true);

  // Track previous location search to detect filter/intake/uni changes
  const prevLocationSearchRef = useRef(location.search);

  // Track previous auth uni to detect university changes
  const prevAuthUniRef = useRef(authUni);

  // Track if config was loaded in previous render
  const prevConfigLoadedRef = useRef(isConfigLoaded);

  // Track if we have fetched totals (even if empty)
  const hasFetchedTotalsRef = useRef(totals !== undefined);
  useEffect(() => {
    hasFetchedTotalsRef.current = totals !== undefined;
  }, [totals]);

  // Track if we have fetched students (even if empty)
  const hasFetchedStudentsRef = useRef(allStudents !== undefined);
  useEffect(() => {
    hasFetchedStudentsRef.current = allStudents !== undefined;
  }, [allStudents]);

  useEffect(() => {
    const currentSearch = location.search;
    const prevSearch = prevLocationSearchRef.current;
    const searchParams = new URLSearchParams(currentSearch);
    const prevSearchParams = new URLSearchParams(prevSearch);

    // Check if we have an intakeId and config is loaded before making any requests
    if (!intakeId || intakeId === '') {
      return;
    }

    // Wait for config to be loaded before making requests (especially important when switching universities)
    if (!isConfigLoaded) {
      return;
    }

    // Additional check: if available intakes is empty, config might still be loading
    if (availableIntakes.length === 0) {
      return;
    }

    // Check if the current intake is valid for the current university
    // This is crucial when switching universities
    let isIntakeValidForUni = true;
    if (isConfigLoaded && availableIntakes.length > 0) {
      isIntakeValidForUni = availableIntakes.some(intake => intake.value === intakeId);
      if (!isIntakeValidForUni) {
        return;
      }
    }

    // Determine if we should request students
    let shouldRequestStudents = false;

    // Use the refs that track whether we've fetched data (even if empty)
    const hasStudentsInStore = hasFetchedStudentsRef.current;

    // Determine if we should request totals
    let shouldRequestTotals = false;

    // Use the refs that track whether we've fetched data (even if empty)
    const hasTotalsInStore = hasFetchedTotalsRef.current;

    if (isInitialLoadRef.current) {
      // This is the initial load of the component
      isInitialLoadRef.current = false;

      if (!hasStudentsInStore) {
        shouldRequestStudents = true;
      }

      if (!hasTotalsInStore) {
        // Request totals on initial page load only if we don't have them
        shouldRequestTotals = true;
      }
    } else {
      // Detailed logging for filter changes (excluding stage for totals calculation)
      const filterChanges: string[] = [];
      const filterChangesExcludingStage: string[] = [];

      for (const [key, value] of searchParams.entries()) {
        const prevValue = prevSearchParams.getAll(key);
        if (!prevValue.includes(value) && key !== 'page' && key !== 'sort-col' && key !== 'sort-dir') {
          filterChanges.push(`${key}: "${prevValue}" → "${value}"`);

          // Track non-stage filter changes separately for totals logic
          if (key !== 'stage') {
            filterChangesExcludingStage.push(`${key}: "${prevValue}" → "${value}"`);
          }
        }
      }

      // Check for removed filters
      for (const [key, value] of prevSearchParams.entries()) {
        const valueRemoved = !searchParams.get(key) || !searchParams.getAll(key).includes(value);
        if (valueRemoved && key !== 'page' && key !== 'sort-col' && key !== 'sort-dir') {
          filterChanges.push(`${key}: "${value}" → removed`);
          // Track non-stage filter changes separately for totals logic
          if (key !== 'stage') {
            filterChangesExcludingStage.push(`${key}: "${value}" → removed`);
          }
        }
      }
      const filtersChanged = filterChanges.length > 0;
      const filtersChangedExcludingStage = filterChangesExcludingStage.length > 0;

      // Check if stage changed (important for students)
      const currentStage = searchParams.get('stage');
      const prevStage = prevSearchParams.get('stage');
      const stageChanged = currentStage !== prevStage;

      // Check if intake changed
      const currentIntakeId = searchParams.get('intakeId');
      const prevIntakeId = prevSearchParams.get('intakeId');
      const intakeChanged = currentIntakeId !== prevIntakeId;

      // Check if uni changed (from auth state, not URL params)
      const prevUni = prevAuthUniRef.current;
      const currentUni = authUni;
      const uniChanged = currentUni !== prevUni;

      // Clear data when university or intake changes so we fetch fresh data
      if (uniChanged || intakeChanged) {
        dispatch(clearStudents());
        // Reset the refs since we've cleared the data
        hasFetchedStudentsRef.current = false;
        hasFetchedTotalsRef.current = false;
      }

      const currentSortCol = searchParams.get('sort-col');
      const prevSortCol = prevSearchParams.get('sort-col');
      const currentSortDir = searchParams.get('sort-dir');
      const prevSortDir = prevSearchParams.get('sort-dir');
      const sortChanged = currentSortCol !== prevSortCol || currentSortDir !== prevSortDir;

      // Students need to be refetched when stage, filters, intake, uni or sort changes
      const needsFreshStudents = stageChanged || filtersChanged || intakeChanged || uniChanged || sortChanged;

      shouldRequestStudents = needsFreshStudents;

      // Totals need to be refetched when filters (excluding stage), intake, or uni changes
      // Totals are the same across all stages, so stage changes don't require refetching
      const needsFreshTotals = filtersChangedExcludingStage || intakeChanged || uniChanged;

      shouldRequestTotals = needsFreshTotals;

      // If we don't have data in store, we should request it regardless of changes
      // BUT only if the intake is valid for the university
      if (!hasStudentsInStore && !shouldRequestStudents && isIntakeValidForUni) {
        shouldRequestStudents = true;
      }

      if (!hasTotalsInStore && !shouldRequestTotals && isIntakeValidForUni) {
        shouldRequestTotals = true;
      }
    }

    // Only dispatch requests if the intake is valid for the current university
    if (isIntakeValidForUni) {
      if (shouldRequestStudents) {
        dispatch(requestStudents());
      }

      if (shouldRequestTotals) {
        dispatch(requestTotals());
      }

      // Filters should be requested with the same logic as totals
      if (shouldRequestTotals) {
        dispatch(requestFilters());
      }
    }

    // Update refs for next comparison
    prevLocationSearchRef.current = currentSearch;
    prevAuthUniRef.current = authUni;
    prevConfigLoadedRef.current = isConfigLoaded;
  }, [location.search, dispatch, intakeId, authIntake, authUni, isConfigLoaded, availableIntakes, navigate]);

  const setIsTaskBarOpened = useCallback((isOpen: boolean) => dispatch(setIsTaskBarOpen(isOpen)), [dispatch]);
  const unpinTagsFromStudents = useCallback(
    ({ tags, studentNumbers }: { tags: { id: string; isProgrammable: boolean }[]; studentNumbers: string[] }) => {
      dispatch(studentsUnpinTags(studentNumbers, tags));
    },
    [dispatch],
  );
  const setIsDropDownDisabled = useCallback(
    (isDisabled: boolean) => dispatch(setIsDropdownDisabled(isDisabled)),
    [dispatch],
  );

  const width = LEFT_BAR_WIDTH;

  const handleSort = useCallback(
    (columnKey: string, direction: any) => {
      setSort({
        col: columnKey,
        direction,
      });
    },
    [setSort],
  );
  const [tagDeleteModalData, setTagDeleteModalData] = useState({
    clickAction: () => {},
    title: '',
    buttonName: '',
  });
  const [isTagDeleteConfirmationModal, setTagDeleteConfirmationModal] = useState(false);
  const [gridColumnsWidth, setGridColumnsWidth] = useState('');

  const onTagDelete = useCallback(
    (tagId: string, tagType: TagType | undefined, row: Row) => {
      setTagDeleteModalData({
        title: 'Remove Tag From Student?',
        buttonName: 'Confirm',
        clickAction: () => {
          unpinTagsFromStudents({
            tags: [{ id: tagId, isProgrammable: tagType === TagType.PROGRAMMABLE }],
            studentNumbers: [row.p_key],
          });
          setTagDeleteConfirmationModal(false);
        },
      });
      setTagDeleteConfirmationModal(true);
    },
    [unpinTagsFromStudents],
  );
  const rows = useCallback(
    () => serializeStudentsToRows(allStudents || [], openedStudentRecords, onTagDelete, currentUserName),
    [allStudents, openedStudentRecords, onTagDelete, currentUserName],
  );

  const enableStudentUserActions = (selectedStudentId: string) => {
    const studentRecord = allStudents?.find((student: IStudentRow) => student.p_key === selectedStudentId);

    if (studentRecord?.pathway) {
      dispatch(getPartner(studentRecord.pathway));
    }

    if (studentRecord.email) {
      dispatch(
        getStudentUserRecord(
          selectedStudentId,
          studentRecord.email,
          studentRecord?.pathway ? studentRecord?.pathway : '',
        ),
      );
    }
  };

  const onSelect = useCallback(
    (checked: boolean, row: Row) => {
      // debugger;
      const selectedStudentId = rowKeyGetter(row);
      const studentIds = checked
        ? [...selectedRows, selectedStudentId]
        : selectedRows.filter(el => el !== selectedStudentId);

      setSelectedRows(studentIds);
      setIsTaskBarOpened(true);
      setIsDropDownDisabled(studentIds.length === 0);

      // If only one student is selected, enable student user actions (reset password or resend invitation) for that student
      if (studentIds.length === 1) {
        enableStudentUserActions(studentIds[0]);
      }
    },
    [
      dispatch,
      isPathway,
      setIsDropDownDisabled,
      setIsTaskBarOpened,
      selectedRows,
      setSelectedRows,
      studentUsers.users,
      allStudents,
    ],
  );
  const columns = useCallback(
    () =>
      getColumnsForActiveTab({
        hasAgentOnlyDocs,
        isAgentUser,
        activeTab,
        isEnrolyUser,
        tabs,
        userRole,
        width,
        selectedRows,
        partners: partners ?? [],
        isImmigrationProvider,
        isOfferEnabled,
        isPathway,
        isServiceProvider,
      }),
    [
      hasAgentOnlyDocs,
      isAgentUser,
      activeTab,
      isEnrolyUser,
      tabs,
      userRole,
      width,
      selectedRows,
      partners,
      isImmigrationProvider,
      isOfferEnabled,
      isPathway,
      isServiceProvider,
    ],
  );

  useEffect(() => {
    if (scrollRowIndex && gridRef?.current && typeof gridRef.current.scrollTo === 'function') {
      gridRef.current.scrollTo(0, scrollRowIndex);
    }
  }, [gridRef?.current]);

  useEffect(() => {
    if (gridRef?.current && typeof gridRef.current.scrollTo === 'function') {
      gridRef.current.scrollTo(0, 0);
    }
  }, [activeTab]);

  useEffect(() => {
    if (resetDropdownStatus) {
      setSelectedRows([]);
    }
  }, [resetDropdownStatus, setSelectedRows]);

  useEffect(() => {
    // Only update tabs when we have actual totals data or when we need to initialize tabs
    // This prevents the counts from flashing to 0 when totals is temporarily undefined
    if (totals !== undefined) {
      const customTabs = makeTabs(totals, configTabs, userRole, pathwayAccess, isPathway ?? false, activeTab);
      dispatch(setTabs(customTabs));
    } else if (tabs.length === 0 && configTabs.length > 0) {
      // Initialize tabs without counts if we don't have any tabs yet
      const customTabs = makeTabs([], configTabs, userRole, pathwayAccess, isPathway ?? false, activeTab);
      dispatch(setTabs(customTabs));
    }
  }, [
    userRole,
    activeTab,
    configTabs,
    width,
    headerTab,
    studentReloaded,
    pathwayAccess,
    isPathway,
    dispatch,
    totals,
    tabs.length,
  ]);

  useEffect(() => {
    let width = '';
    (columns() || []).forEach(column => {
      width = `${width} ${column.width ?? 'minmax(136px, 1.25fr)'}`;
    });
    setGridColumnsWidth(width);
  }, [columns]);

  const handleScroll = useDebouncedCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const lastChild = target.lastChild as HTMLElement;
    if (lastChild && lastChild.children && lastChild.children[0]) {
      const rowIdx = Math.round((lastChild.children[0] as HTMLElement).offsetTop - 190);
      if (rowIdx) updateScrollRowIndex(rowIdx);
    }
  }, 100);

  const rowKeyGetter = useCallback((row: Row) => row.p_key, []);

  const sortCol = params.get('sort-col') ?? '';
  const sortDirection = params.get('sort-dir') as SortDirection;

  return (
    <>
      <AlertDialog
        isOpen={isTagDeleteConfirmationModal}
        modalTitle={`Remove tag`}
        descriptionText={`Are you sure you want to remove this tag?`}
        confirmationButtonText={`Remove`}
        id={'remove-tag'}
        onClickSuccess={() => tagDeleteModalData.clickAction()}
        onClose={() => setTagDeleteConfirmationModal(false)}
      />

      <CASToolbar />

      <Box // GridContainer
        ref={gridRef}
        onScroll={handleScroll}
        flexGrow={1}
        overflow={'auto'}
        overscrollBehaviorX={'contain'}
        pb={{ base: 'navbarHeight', md: 0 }}
        sx={{
          '-webkit-overflow-scrolling': 'touch',
        }}
      >
        <Box // GridContent
          className="grid-Container"
          role="grid"
          aria-multiselectable="true"
          aria-colcount={(columns() || []).length}
          aria-rowcount={rows().length}
          display={'table'}
          minWidth={'100%'}
          sx={{
            contain: 'strict',
          }}
        >
          <Box //GridHeader
            role="row"
            aria-rowindex={1}
            ref={gridHeaderRef}
            bgColor={'chakra-body-bg'}
            color={'muted'}
            borderBottomWidth={1}
            display={'grid'}
            position={'sticky'}
            top={0}
            height={'32px'}
            gridTemplateColumns={gridColumnsWidth}
            zIndex={4}
            sx={{
              contain: 'size layout style paint',
            }}
          >
            {(columns() || []).map(column => {
              if (column.headerRenderer) {
                return (
                  <HeaderItem key={column.key} isSorted={sortCol === column.key && sortDirection !== 'NONE'}>
                    <column.headerRenderer
                      column={column}
                      sortColumn={sortCol}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                      allRowsSelected={(selectedRows?.length || 0) === rows().length}
                      onAllRowsSelectionChange={checked => {
                        const newSelectedRows = checked ? rows().map(rowKeyGetter) : [];
                        setIsDropDownDisabled(!checked);
                        setSelectedRows(newSelectedRows);
                      }}
                    />
                  </HeaderItem>
                );
              }
              return <HeaderItem key={column.key}>{column.name}</HeaderItem>;
            })}
          </Box>
          {rows().map((row, index) => {
            const isRowSelected = allSelected || selectedRows.includes(rowKeyGetter(row));
            return (
              <RowEventsHandler
                index={index}
                rowProps={row}
                key={rowKeyGetter(row)}
                gridColumnsWidth={gridColumnsWidth}
                isRowSelected={isRowSelected}
              >
                {columns().map((column, colIndex) => {
                  const isSorted = sortCol === column.key && sortDirection !== 'NONE';
                  return (
                    <Box //RowItem
                      className="grid-Cell"
                      role={colIndex === 1 ? 'rowheader' : 'gridcell'}
                      aria-colindex={colIndex + 1}
                      key={rowKeyGetter(row) + column.key}
                      height={'100%'}
                      overflow={'hidden'}
                      px={2}
                      py={4}
                      fontSize={'sm'}
                      lineHeight={4}
                      bgColor={isSorted ? 'utility.50' : null}
                      sx={{
                        '&:first-child': {
                          zIndex: 1,
                          p: 0,
                          bgColor: 'inherit',
                          position: 'sticky',
                          width: '32px',
                          left: 0,
                        },

                        '&:nth-child(2)': {
                          zIndex: 1,
                          position: 'sticky',
                          left: '32px',
                          p: 0,
                          bgColor: isSorted ? null : 'inherit',
                        },
                      }}
                    >
                      {column.formatter && (
                        <>
                          <column.formatter
                            column={column}
                            rowIdx={Number(column.key)}
                            row={row}
                            isCellSelected={false}
                            isRowSelected={isRowSelected}
                            onRowSelectionChange={checked => onSelect(checked, row)}
                          />
                        </>
                      )}
                    </Box>
                  );
                })}
              </RowEventsHandler>
            );
          })}
        </Box>
      </Box>
    </>
  );
};

const HeaderItem = (props: { children: React.ReactNode; isSorted?: boolean }) => (
  <Box
    className="grid-HeaderCell"
    role="columnheader"
    letterSpacing={'tight'}
    lineHeight={4}
    px={2}
    py={2}
    bgColor={props.isSorted ? 'utility.50' : 'chakra-body-bg'}
    sx={{
      '&:first-child': {
        bgColor: 'inherit',
        position: 'sticky',
        left: 0,
        width: '32px',
        height: '100%',
        p: 0,
      },
      '&:nth-child(2)': {
        bgColor: 'inherit',
        position: 'sticky',
        left: '32px',
        borderRightWidth: 1,
        pl: 0,
      },
    }}
    {...props}
  >
    {props.children}
  </Box>
);

export default CustomDataGridComp;
